import { request } from "../../service/api";
import { CHECK_CUSTOMER_NUMBER, CUSTOM_FORM_LIST, DELETE_FORM, SUCCESS_STATUS } from "../../constants";
import { constructEndpoint } from "../../utils/commonHelper";
import type { ApiEnvelope } from "../../types/api";
import type { AssignEmailOption, CreateFormPayload, CustomFormItem, FacilitiesData, UpdateFormDetailsPayload } from "./types";

/**
 * Deletes multiple custom forms in parallel using Promise.all.
 */
export function deleteCustomForms(items: Array<{ id: number }>): Promise<ApiEnvelope<void>[]> {
  return Promise.all(
    items.map((item) => request(constructEndpoint(DELETE_FORM, item.id), { method: "DELETE" }) as Promise<ApiEnvelope<void>>)
  );
}

/**
 * GET /facilities?customerNumber=... for the Assign tab's organization picker.
 */
export function fetchFacilitiesByCustomerNumber(customerNumber: string): Promise<ApiEnvelope<FacilitiesData>> {
  return request(`${CHECK_CUSTOMER_NUMBER}?customerNumber=${customerNumber}`, { method: "GET" }) as Promise<ApiEnvelope<FacilitiesData>>;
}

/**
 * GET /listoptions/useremail?search=... typeahead for the Assign tab's Survey email picker.
 */
export async function searchAssignEmails(query: string): Promise<AssignEmailOption[]> {
  if (!query) return [];
  try {
    const data = (await request(constructEndpoint(`/listoptions/useremail?search=${query}`), {
      method: "GET",
    })) as ApiEnvelope<{ userEmail?: Array<{ id: number; email: string }> }>;
    if (data?.status !== SUCCESS_STATUS) return [];
    const userEmail = data?.data?.userEmail;
    return Array.isArray(userEmail) ? userEmail.map((item) => ({ value: item.id, label: item.email })) : [];
  } catch {
    return [];
  }
}

/**
 * POST /forms for the Create Form modal.
 */
export function createCustomForm(payload: CreateFormPayload): Promise<ApiEnvelope<CustomFormItem>> {
  return request(CUSTOM_FORM_LIST, {
    method: "POST",
    body: JSON.stringify(payload),
  }) as Promise<ApiEnvelope<CustomFormItem>>;
}

/**
 * PUT /customform/:id -- shared with the design canvas (Ticket 3 scope), which keeps
 */
export function updateFormDetails(payload: UpdateFormDetailsPayload): Promise<ApiEnvelope<UpdateFormDetailsPayload>> {
  return request(`${CUSTOM_FORM_LIST}/${payload.id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }) as Promise<ApiEnvelope<UpdateFormDetailsPayload>>;
}
